import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useMemo } from 'react'

import { BASE_URL } from '../../../../ini.json'
import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { sanitizeFileName } from '../../../common/pdfHelpers'
import { useAllBuybackCalculationsQuery } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { stringSorter, utcDateSorter } from '../../utils/generalTableHelpers'
import { tablePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useTableLocale } from '../../utils/useTableLocale'
import { Min } from '../DateFormat/DateFormat'
import { LocalSavedPdf } from '../LocalSavedPdf/LocalSavedPdf'
import { LocalSavedPdfDrawerPreview } from '../LocalSavedPdfDrawerPreview/LocalSavedPdfDrawerPreview'
import { PropertyDrawerLink } from '../PropertyDrawerLink/PropertyDrawerLink'

export interface BuybackTableItem {
  id: string
  accountId: string
  propertyId: string
  propertyType: string
  propertyName: string
  title?: string
  description?: string
  createdAt: Date
  updatedAt?: Date
  calculatorVersion?: string
  priceEstimateId?: string
  cadastralCode?: string
  lv?: string
  formData: string | Record<string, unknown>
}

export interface BuybackTableProps {
  initialProp?: unknown
}

const tableId = 'buyback-table'
const scrollX = { x: '1300px' }

export const BuybackTable: FunctionComponent<BuybackTableProps> = () => {
  const tenantId = useAppSelector(state => state.myProfile.tenantId)

  const { searchFilter, dateFilter } = useColumnFilters<BuybackTableItem>()

  const [queryResult] = useAllBuybackCalculationsQuery({
    variables: { accountId: tenantId },
  })

  const dataSource = useMemo(
    () =>
      (queryResult.data?.allBuybackCalculations?.nodes as BuybackTableItem[]) ||
      [],
    [queryResult.data?.allBuybackCalculations?.nodes]
  )

  const columns = useMemo<ColumnsType<BuybackTableItem>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        responsive: ['' as Breakpoint],
      },
      {
        title: 'Vytvořeno',
        dataIndex: 'createdAt',
        width: 130,
        sorter: utcDateSorter('createdAt'),
        ...dateFilter('createdAt'),
        render: createdAt => <Min>{createdAt}</Min>,
      },
      {
        title: 'Typ',
        dataIndex: 'propertyType',
        width: 100,
        className: 'block first-letter:uppercase',
        sorter: stringSorter('propertyType'),
        ...searchFilter('propertyType', 'Hledat typ'),
      },
      {
        title: 'Nemovitost',
        dataIndex: 'propertyName',
        sorter: stringSorter('propertyName'),
        ...searchFilter('propertyName', 'Hledat název'),
        render: (propertyName, record) => (
          <PropertyDrawerLink
            propertyId={record.propertyId}
            propertyType={record.propertyType as PROPERTY_TYPES}
          >
            {propertyName || '- Neuvedeno -'}
          </PropertyDrawerLink>
        ),
      },
      {
        title: 'Název výpočtu',
        dataIndex: 'title',
        sorter: stringSorter('title'),
        ...searchFilter('title', 'Hledat název'),
      },
      {
        title: 'Popis',
        dataIndex: 'description',
        ellipsis: true,
        ...searchFilter('description', 'Hledat v popisu'),
        render: (_, record) => (
          <div className="block truncate" title={record.description}>
            {record.description}
          </div>
        ),
      },
      {
        title: 'Akce',
        dataIndex: 'actions',
        width: 140,
        className: '!text-right',
        render: (_, record) => (
          <div className="space-x-2 text-right">
            <LocalSavedPdfDrawerPreview
              buttonProps={{ size: 'small', type: 'link' }}
              drawerTitle="PDF náhled kalkulace"
              fileName={sanitizeFileName(record.title, 'kalkulace-')}
              path={`${BASE_URL}/download/${record.accountId}/buyback/${record.id}.pdf`}
              buttonText="zobrazit PDF"
            >
              <LocalSavedPdf
                path={`${BASE_URL}/download/${record.accountId}/buyback/${record.id}.pdf`}
              />
            </LocalSavedPdfDrawerPreview>
          </div>
        ),
      },
    ],
    [dateFilter, searchFilter]
  )

  return (
    <Table
      id={tableId}
      rowKey="id"
      rowClassName="animate-fadeIn"
      size="small"
      scroll={scrollX}
      dataSource={dataSource}
      columns={columns}
      loading={queryResult.fetching}
      locale={useTableLocale(queryResult.fetching, 10)}
      pagination={tablePagination}
    />
  )
}
